import { useState, useEffect } from 'react';
import { getAdminCategories, createCategory, updateCategory, deleteCategory } from '../lib/api';
import { Plus, PencilSimple, Trash, X, Check, FloppyDisk, Tag, TreeStructure } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export default function CategoryManagement() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ category: '', subcategory: '', display_order: 0 });

    const load = () => {
        setLoading(true);
        getAdminCategories()
            .then(r => setCategories(r.data.categories))
            .catch(() => toast.error('Failed to load categories'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        if (!form.category.trim()) { toast.error('Category name required'); return; }
        try {
            if (editId) {
                await updateCategory(editId, form);
                toast.success('Category updated');
            } else {
                await createCategory(form);
                toast.success('Category created');
            }
            setShowAdd(false);
            setEditId(null);
            setForm({ category: '', subcategory: '', display_order: 0 });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            await deleteCategory(id);
            toast.success('Category deleted');
            load();
        } catch { toast.error('Failed to delete'); }
    };

    const startEdit = (cat) => {
        setEditId(cat.id);
        setForm({ category: cat.category, subcategory: cat.subcategory || '', display_order: cat.display_order || 0 });
        setShowAdd(true);
    };

    // Group categories
    const grouped = {};
    categories.forEach(c => {
        if (!grouped[c.category]) grouped[c.category] = [];
        if (c.subcategory) grouped[c.category].push(c);
    });

    return (
        <div className="space-y-6" data-testid="category-management">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Category Management</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage product categories and subcategories</p>
                </div>
                <Button onClick={() => { setEditId(null); setForm({ category: '', subcategory: '', display_order: 0 }); setShowAdd(true); }} data-testid="add-category-btn">
                    <Plus size={16} className="mr-2" /> Add Category
                </Button>
            </div>

            <div className="bg-card border border-border rounded-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-8">#</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Subcategory</TableHead>
                            <TableHead className="text-right">Order</TableHead>
                            <TableHead className="w-24"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} className="h-20 text-center text-muted-foreground">Loading...</TableCell></TableRow>
                        ) : categories.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="h-20 text-center text-muted-foreground">No categories found</TableCell></TableRow>
                        ) : categories.map((cat, i) => (
                            <TableRow key={cat.id} data-testid={`cat-row-${cat.id}`}>
                                <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <Tag size={14} className="text-muted-foreground" />
                                        <span className="font-medium text-sm">{cat.category}</span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {cat.subcategory ? (
                                        <div className="flex items-center gap-1.5">
                                            <TreeStructure size={12} className="text-muted-foreground" />
                                            <span className="text-sm">{cat.subcategory}</span>
                                        </div>
                                    ) : <span className="text-xs text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-xs">{cat.display_order || 0}</TableCell>
                                <TableCell>
                                    <div className="flex gap-1 justify-end">
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                                            <PencilSimple size={14} />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(cat.id)} data-testid={`del-cat-${cat.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Add/Edit dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Category' : 'Add Category'}</DialogTitle>
                        <DialogDescription>{editId ? 'Update category details.' : 'Create a new category or subcategory.'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Category Name</Label>
                            <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Chopping Boards" data-testid="cat-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Subcategory (optional)</Label>
                            <Input value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))} placeholder="e.g. Round Chopping Board" data-testid="cat-sub-input" />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Display Order</Label>
                            <Input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))} className="w-24 font-mono" />
                        </div>
                        <Button onClick={handleSave} className="w-full" data-testid="save-cat-btn">
                            <FloppyDisk size={16} className="mr-2" /> {editId ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
